import React from "react";
import * as jsxRuntime from "react/jsx-runtime";
import * as jsxDevRuntime from "react/jsx-dev-runtime";
import FiberDebugger from "./fibber-debugger";

export const DEBUG_ERROR_KEY = "debugerror";
// Store debug information for all elements (with Fiber node as key)
const fiberDebugMap = new WeakMap<any, () => Error>();
// Store debug information for native DOM elements (with DOM element as key, for fast lookup)
const nativeElementDebugMap = new WeakMap<HTMLElement, () => Error>();

export function interceptReactJSX() {
  // Save original functions
  const originalCreateElement = React.createElement;
  const originalJsx = (jsxRuntime as any).jsx;
  const originalJsxs = (jsxRuntime as any).jsxs;
  const originalJsxDEV = (jsxDevRuntime as any).jsxDEV;

  const createElementDebugError = () => {
    const error = new Error();
    return () => error;
  };

  // Check if it's a native DOM element (string type)
  const isNativeDOMElement = (type: any): boolean => {
    return typeof type === 'string';
  };

  // Intercept React.createElement
  (React as any).createElement = function (...args: [any, any?, ...any[]]) {
    const type = args[0];
    const debugErrorFn = createElementDebugError();
    
    if (!args[1]) {
      args[1] = {};
    }
    
    if (isNativeDOMElement(type)) {
      // For native DOM elements, use ref to store in WeakMap
      const originalRef = args[1].ref;
      args[1].ref = (element: HTMLElement | null) => {
        if (element) {
          nativeElementDebugMap.set(element, debugErrorFn);
          // Also store via Fiber node
          const fiber = FiberDebugger.getFiberFromDOMNode(element);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        if (typeof originalRef === 'function') {
          originalRef(element);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = element;
        }
      };
    } else {
      // For React components, don't add to props to avoid passing to DOM
      // Try to find the final DOM element via ref and store it
      const originalRef = args[1].ref;
      args[1].ref = (instance: any) => {
        if (instance instanceof HTMLElement) {
          // If ref is directly a DOM element
          nativeElementDebugMap.set(instance, debugErrorFn);
          const fiber = FiberDebugger.getFiberFromDOMNode(instance);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        
        if (typeof originalRef === 'function') {
          originalRef(instance);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = instance;
        }
      };
    }
    
    return originalCreateElement(...args);
  };

  // Intercept jsx
  (jsxRuntime as any).jsx = function (...args: [any, any]) {
    const type = args[0];
    const debugErrorFn = createElementDebugError();
    
    if (!args[1]) {
      args[1] = {};
    }
    
    if (isNativeDOMElement(type)) {
      // For native DOM elements, use ref to store in WeakMap
      const originalRef = args[1].ref;
      args[1].ref = (element: HTMLElement | null) => {
        if (element) {
          nativeElementDebugMap.set(element, debugErrorFn);
          // Also store via Fiber node
          const fiber = FiberDebugger.getFiberFromDOMNode(element);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        if (typeof originalRef === 'function') {
          originalRef(element);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = element;
        }
      };
    } else {
      // For React components, don't add to props to avoid passing to DOM
      // Try to find the final DOM element via ref and store it
      const originalRef = args[1].ref;
      args[1].ref = (instance: any) => {
        if (instance instanceof HTMLElement) {
          // If ref is directly a DOM element
          nativeElementDebugMap.set(instance, debugErrorFn);
          const fiber = FiberDebugger.getFiberFromDOMNode(instance);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        
        if (typeof originalRef === 'function') {
          originalRef(instance);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = instance;
        }
      };
    }
    
    return originalJsx(...args);
  };

  // Intercept jsxs
  (jsxRuntime as any).jsxs = function (...args: any[]) {
    const type = args[0];
    const debugErrorFn = createElementDebugError();
    
    if (!args[1]) {
      args[1] = {};
    }
    
    if (isNativeDOMElement(type)) {
      // For native DOM elements, use ref to store in WeakMap
      const originalRef = args[1].ref;
      args[1].ref = (element: HTMLElement | null) => {
        if (element) {
          nativeElementDebugMap.set(element, debugErrorFn);
          // Also store via Fiber node
          const fiber = FiberDebugger.getFiberFromDOMNode(element);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        if (typeof originalRef === 'function') {
          originalRef(element);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = element;
        }
      };
    } else {
      // For React components, don't add to props to avoid passing to DOM
      // Try to find the final DOM element via ref and store it
      const originalRef = args[1].ref;
      args[1].ref = (instance: any) => {
        if (instance instanceof HTMLElement) {
          // If ref is directly a DOM element
          nativeElementDebugMap.set(instance, debugErrorFn);
          const fiber = FiberDebugger.getFiberFromDOMNode(instance);
          if (fiber) {
            fiberDebugMap.set(fiber, debugErrorFn);
          }
        }
        
        if (typeof originalRef === 'function') {
          originalRef(instance);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          (originalRef as React.RefObject<any>).current = instance;
        }
      };
    }
    
    return originalJsxs(...args);
  };

  // Intercept jsxDEV
  if (originalJsxDEV) {
    (jsxDevRuntime as any).jsxDEV = function (...args: any[]) {
      const type = args[0];
      const debugErrorFn = createElementDebugError();
      
      if (!args[1]) {
        args[1] = {};
      }
      
      if (isNativeDOMElement(type)) {
        // For native DOM elements, use ref to store in WeakMap
        const originalRef = args[1].ref;
        args[1].ref = (element: HTMLElement | null) => {
          if (element) {
            nativeElementDebugMap.set(element, debugErrorFn);
            const fiber = FiberDebugger.getFiberFromDOMNode(element);
            if (fiber) {
              fiberDebugMap.set(fiber, debugErrorFn);
            }
          }
          if (typeof originalRef === 'function') {
            originalRef(element);
          } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
            (originalRef as React.RefObject<any>).current = element;
          }
        };
      } else {
        // For React components, don't add to props to avoid passing to DOM
        const originalRef = args[1].ref;
        args[1].ref = (instance: any) => {
          // Delay finding DOM element because the component may not be fully rendered yet
          setTimeout(() => {
            let domElement: HTMLElement | null = null;
            
            if (instance instanceof HTMLElement) {
              domElement = instance;
            } else if (instance && typeof instance === 'object') {
              // Try to find DOM element from component instance
              if (instance instanceof HTMLElement) {
                domElement = instance;
              } else {
                // Find via Fiber
                const fiber = FiberDebugger.getFiberFromDOMNode(instance as any);
                if (fiber?.stateNode instanceof HTMLElement) {
                  domElement = fiber.stateNode;
                }
              }
            }
            
            if (domElement) {
              nativeElementDebugMap.set(domElement, debugErrorFn);
              const fiber = FiberDebugger.getFiberFromDOMNode(domElement);
              if (fiber) {
                fiberDebugMap.set(fiber, debugErrorFn);
              }
            }
          }, 0);
          
          if (typeof originalRef === 'function') {
            originalRef(instance);
          } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
            (originalRef as React.RefObject<any>).current = instance;
          }
        };
      }
      
      return originalJsxDEV(...args);
    };
  }
}

export function getDebugErrorFromSelector(selector: string) {
  const element = document.querySelector(selector);
  if (!element) {
    return null;
  }
  
  // First try to get from WeakMap (native DOM elements)
  const nativeDebugErrorFn = nativeElementDebugMap.get(element as HTMLElement);
  if (nativeDebugErrorFn) {
    return {
      element,
      debugError: nativeDebugErrorFn(),
    };
  }
  
  // Then try to get via Fiber node
  const fiber = FiberDebugger.getFiberFromDOMNode(element as HTMLElement);
  if (fiber) {
    const fiberDebugErrorFn = fiberDebugMap.get(fiber);
    if (fiberDebugErrorFn) {
      return {
        element,
        debugError: fiberDebugErrorFn(),
      };
    }
  }
  
  return null;
}